import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { GetJsDataInfo } from '@bingads-webui-clientcenter/js-data-cc-rest-base';

export const CcJsDataContext = createContext(null);

export const CcJsDataContextConsumer = CcJsDataContext.Consumer;

export const withCcJsData = (WrappedComponent) => {
  const HoC = props => (
    <CcJsDataContext.Consumer>
      {ccJsData => <WrappedComponent {...props} ccJsData={ccJsData} />}
    </CcJsDataContext.Consumer>
  );
  HoC.displayName = `withCcJsData(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};

export const CcJsDataContextProvider = ({
  customerId,
  userId,
  appendErrorSummary,
  handleAjaxAuthFailure,
  clientCenterBaseUrl,
  initialJsData,
  children,
  resourceNames,
}) => {
  const getJsData = () => {
    if (initialJsData) {
      return initialJsData;
    }

    const options = {
      cidFetcher: () => customerId,
      uidFetcher: () => userId,
      appendErrorSummary,
      handleAjaxAuthFailure,
      willSupportCors: true,
    };

    return GetJsDataInfo(options, clientCenterBaseUrl, resourceNames).jsDataCcRest;
  };

  const jsData = useMemo(getJsData, [customerId, userId, clientCenterBaseUrl, initialJsData, resourceNames]);

  return (
    <CcJsDataContext.Provider value={jsData}>
      {children}
    </CcJsDataContext.Provider>
  );
};

CcJsDataContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  customerId: PropTypes.number,
  userId: PropTypes.number,
  clientCenterBaseUrl: PropTypes.string,
  appendErrorSummary: PropTypes.func,
  handleAjaxAuthFailure: PropTypes.func,
  initialJsData: PropTypes.shape({}),
  resourceNames: PropTypes.arrayOf(PropTypes.string),
};

CcJsDataContextProvider.defaultProps = {
  customerId: null,
  userId: null,
  clientCenterBaseUrl: null,
  appendErrorSummary: _.noop,
  handleAjaxAuthFailure: _.noop,
  initialJsData: null,
  resourceNames: [],
};
